// src/components/admin/page-sections/CtaSection.js
import React, { useRef, useEffect } from 'react';
import { Phone } from 'lucide-react';

const CtaSection = ({ content }) => {
  const {
    title = 'Ready to Elevate Your Brand?',
    description = 'Join hundreds of successful businesses who trust us with their packaging needs. Get started with your custom solution today.',
    backgroundColor = '#013263',
    textColor = '#ffffff',
    primaryButtonText = 'Browse Products',
    primaryButtonLink = '/products',
    primaryButtonColor = '#ffffff',
    primaryButtonTextColor = '#1e40af',
    showPrimaryButton = true,
    secondaryButtonText = 'Contact Us',
    secondaryButtonLink = 'tel:+18333333101',
    secondaryButtonColor = '#b45309',
    secondaryButtonTextColor = '#ffffff',
    showSecondaryButton = true,
    phoneNumber = '+18333333101',
    showPhoneIcon = true,
    gradientEnabled = true,
    gradientDirection = 'to right',
    paddingTop = '8rem',
    paddingBottom = '10rem',
    marginBottom = '5rem',
    animationEnabled = true,
    animationStagger = true,
    showBackgroundEffects = true
  } = content;

  const sectionRef = useRef(null);

  // Setup intersection observer for animations
  useEffect(() => {
    if (!animationEnabled) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const children = entry.target.children;
          Array.from(children).forEach((child, index) => {
            setTimeout(() => {
              child.style.opacity = '1';
              child.style.transform = 'translateY(0)';
            }, animationStagger ? index * 200 : 0);
          });
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );
    
    if (sectionRef.current) {
      // Set initial styles
      Array.from(sectionRef.current.children).forEach(child => {
        child.style.opacity = '0';
        child.style.transform = 'translateY(20px)';
        child.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
      });
      
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [animationEnabled, animationStagger]);

  return (
    <div 
      className="relative overflow-hidden" 
      style={{ 
        backgroundColor,
        paddingTop,
        paddingBottom,
        marginBottom
      }}
    >
      {gradientEnabled && (
        <div 
          className="absolute inset-0" 
          style={{ 
            background: `linear-gradient(${gradientDirection}, ${backgroundColor}, #012345)` 
          }}
        ></div>
      )}
      
      {showBackgroundEffects && (
        <>
          <div 
            className="absolute top-0 left-0 right-0 h-1/3"
            style={{
              background: 'linear-gradient(to bottom, rgba(1,50,99,0.8) 0%, rgba(1,50,99,0) 100%)'
            }}
          ></div>
          <div 
            className="absolute bottom-0 left-0 right-0 h-1/3"
            style={{
              background: 'linear-gradient(to top, rgba(1,50,99,0.8) 0%, rgba(1,50,99,0) 100%)'
            }}
          ></div>
        </>
      )}
      
      <div 
        ref={sectionRef}
        className="container mx-auto px-4 text-center relative z-10"
        style={{ color: textColor }}
      >
        <h2 className="text-3xl md:text-4xl font-bold mb-6">{title}</h2>
        <p className="text-xl mb-8 max-w-2xl mx-auto" style={{ color: `${textColor}CC` }}>
          {description}
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          {showPrimaryButton && primaryButtonText && (
            <button
              onClick={() => window.location.href = primaryButtonLink}
              className="group px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              style={{
                backgroundColor: primaryButtonColor,
                color: primaryButtonTextColor
              }}
            >
              <span className="flex items-center justify-center gap-2">
                <span>{primaryButtonText}</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transform transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </span>
            </button>
          )}
          
          {showSecondaryButton && secondaryButtonText && (
            <a
              href={secondaryButtonLink}
              className="group px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              style={{
                backgroundColor: secondaryButtonColor,
                color: secondaryButtonTextColor
              }}
            >
              <span className="flex items-center justify-center gap-2">
                {showPhoneIcon && <Phone size={20} className="transform transition-all group-hover:rotate-12" />}
                <span>{secondaryButtonText}</span>
              </span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default CtaSection;






// // very simple
// // src/components/admin/page-sections/CtaSection.js
// import React from 'react';
// import { Link } from 'react-router-dom';
// import '../../../styles/sections.css';

// const CtaSection = ({ content }) => {
//   // Extract properties with defaults
//   const {
//     ctaTitle,
//     ctaText,
//     ctaButtonText,
//     ctaButtonLink,
//     backgroundImageUrl,
//     overlay = {},
//     buttonStyle = 'primary',
//     buttonSize = 'medium',
//     alignment = 'center',
//     contentWidth = 'medium',
//     verticalPadding = 'medium',
//     openInNewTab = false
//   } = content || {};

//   // Generate class names based on props
//   const containerClasses = `
//     flex items-center justify-center
//     ${getVerticalPaddingClass(verticalPadding)}
//   `;

//   const contentClasses = `
//     ${getContentWidthClass(contentWidth)}
//     ${getAlignmentClass(alignment)}
//   `;

//   const buttonClasses = `
//     ${getButtonStyleClass(buttonStyle)}
//     ${getButtonSizeClass(buttonSize)}
//     inline-block rounded font-medium
//   `;

//   // Generate container styles
//   const containerStyle = {};
  
//   // Add background image if provided
//   if (backgroundImageUrl) {
//     containerStyle.backgroundImage = `url(${backgroundImageUrl})`;
//     containerStyle.backgroundSize = 'cover';
//     containerStyle.backgroundPosition = 'center';
//     containerStyle.position = 'relative';
//   }

//   // Set overlay if enabled
//   let overlayElement = null;
//   if (overlay.enabled && backgroundImageUrl) {
//     overlayElement = (
//       <div 
//         className="absolute inset-0"
//         style={{
//           backgroundColor: overlay.color || 'rgba(0,0,0,0.5)',
//           mixBlendMode: overlay.blendMode || 'normal'
//         }}
//       ></div>
//     );
//   }

//   // Determine if Link should be internal or external
//   const LinkComponent = ctaButtonLink && (
//     ctaButtonLink.startsWith('http') || ctaButtonLink.startsWith('https://') || ctaButtonLink.startsWith('www.')
//   ) ? 'a' : Link;

//   // Link props for external vs internal links
//   const linkProps = LinkComponent === 'a' 
//     ? { 
//         href: ctaButtonLink,
//         target: openInNewTab ? '_blank' : undefined,
//         rel: openInNewTab ? 'noopener noreferrer' : undefined
//       }
//     : { to: ctaButtonLink || '/' };

//   return (
//     <div className={containerClasses} style={containerStyle}>
//       {overlayElement}
      
//       <div className={contentClasses} style={{ position: backgroundImageUrl ? 'relative' : 'static', zIndex: 1 }}>
//         {ctaTitle && (
//           <h2 className="text-2xl md:text-3xl font-bold mb-4">
//             {ctaTitle}
//           </h2>
//         )}
        
//         {ctaText && (
//           <div className="mb-6 max-w-2xl mx-auto">
//             <p className="text-lg">{ctaText}</p>
//           </div>
//         )}
        
//         {ctaButtonText && ctaButtonLink && (
//           <LinkComponent
//             {...linkProps}
//             className={buttonClasses}
//           >
//             {ctaButtonText}
//           </LinkComponent>
//         )}
//       </div>
//     </div>
//   );
// };

// // Helper functions to generate classes
// function getVerticalPaddingClass(size) {
//   switch (size) {
//     case 'small': return 'py-4 md:py-6';
//     case 'medium': return 'py-8 md:py-12';
//     case 'large': return 'py-12 md:py-16';
//     case 'xlarge': return 'py-16 md:py-24';
//     default: return 'py-8 md:py-12';
//   }
// }

// function getContentWidthClass(width) {
//   switch (width) {
//     case 'narrow': return 'max-w-md mx-auto';
//     case 'medium': return 'max-w-2xl mx-auto';
//     case 'wide': return 'max-w-4xl mx-auto';
//     case 'full': return 'w-full px-4';
//     default: return 'max-w-2xl mx-auto';
//   }
// }

// function getAlignmentClass(alignment) {
//   switch (alignment) {
//     case 'left': return 'text-left';
//     case 'center': return 'text-center';
//     case 'right': return 'text-right';
//     default: return 'text-center';
//   }
// }

// function getButtonStyleClass(style) {
//   switch (style) {
//     case 'primary': return 'bg-blue-600 hover:bg-blue-700 text-white';
//     case 'secondary': return 'bg-gray-600 hover:bg-gray-700 text-white';
//     case 'outline': return 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50';
//     case 'link': return 'text-blue-600 hover:text-blue-800 underline';
//     default: return 'bg-blue-600 hover:bg-blue-700 text-white';
//   }
// }

// function getButtonSizeClass(size) {
//   switch (size) {
//     case 'small': return 'px-4 py-1 text-sm';
//     case 'medium': return 'px-6 py-2';
//     case 'large': return 'px-8 py-3 text-lg';
//     default: return 'px-6 py-2';
//   }
// }

// export default CtaSection;




